import { useCallback, useEffect, useState } from "react";
import { type Dataset, listDatasets } from "@/api";
import { type LoadState, resolveLoadErrorMessage } from "@/components/dataset/loadState";

/** 분량 상위 카드가 보여 주는 데이터셋 수. */
export const TOP_DURATION_DATASET_LIMIT = 5;

export type TopDurationDatasetsController = {
  state: LoadState<readonly Dataset[]>;
  reload: () => void;
};

/**
 * 총 수집 길이 상위 데이터셋을 로드한다.
 *
 * `total_duration` 서버 정렬(ZERG-350)을 쓴다. BE 는 미측정(NULL) 행을 DB 기본 순서로 둔다.
 * 그래서 내림차순에서 그 행이 맨 위에 온다. 이것은 의도된 결정이다. 정렬로 숨기지 않고 값을
 * 먼저 채운다. 길이 backfill(ZERG-158)이 진행 중이라 미측정 행이 많다. 그대로 받으면 카드가
 * 대시로 채워진다. `duration_min` 이 같은 값(총 분량, 초)을 거르므로 1초 하한으로 측정된 행만
 * 받는다. BE 가 나중에 NULLS LAST 로 바꿔도 이 쿼리는 계속 맞다.
 *
 * 골격은 `useRecentDatasets` 와 같다. 다시 조회하는 동안에도 직전 목록을 유지해 깜빡이지 않는다.
 */
export function useTopDurationDatasets(): TopDurationDatasetsController {
  const [state, setState] = useState<LoadState<readonly Dataset[]>>({ status: "idle" });
  const [reloadToken, setReloadToken] = useState(0);

  const reload = useCallback(() => {
    setReloadToken((token) => token + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      setState((previous) => (previous.status === "success" ? previous : { status: "loading" }));
      try {
        const page = await listDatasets({
          page: 1,
          size: TOP_DURATION_DATASET_LIMIT,
          status: "available",
          sort: "total_duration",
          order: "desc",
          duration_min: "1",
        });
        if (cancelled) {
          return;
        }
        setState({ status: "success", data: page.items });
      } catch (error) {
        if (cancelled) {
          return;
        }
        setState({ status: "error", message: resolveLoadErrorMessage(error) });
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [reloadToken]);

  return { state, reload };
}
